import { IFollowupAdminMigration } from "@interfaces/screens/followup-admin.interface";
import { BasePresenter } from "@presenters/base/base.presenter";
import { useSelector } from "react-redux";
import { IFollowup, FollowupMigration } from "../../models/index";
import { FollowupService, IFollowupService } from "@services/proyecta/followup.service";
import { read, utils, writeFileXLSX } from 'xlsx';
import XLSX from 'sheetjs-style'; 
import { Utilities, Enums} from "@utils";

export class AdminFollowupMigration extends BasePresenter<IFollowupAdminMigration> {
    private followupService: IFollowupService;
    public followupData: IFollowup[];
    public formMasters: any;
    public migrationData: any;
    private migrationSheets = [
        { id:"indLEPpal", name:"Indicadores LE ppal"},
        { id:"indLESec", name:"Indicadores LE sec"},
        { id:"indLEResI", name:"Avance Indicadores RI"},
        { id:"indLEProd", name:"Avance Indicadores Producto"},
        { id:"indObj", name:"Indicadores Objetivo Especifico"},
        { id:"indResEsp", name:"Indicadores Resultado Esperado"},
        { id:"indAct", name:"Indicadores Actividad"},
        { id:"beneficiaries", name:"Avance Beneficiarios"},
        { id:"budgetImp", name:"Presupuesto - Implementador"},
        { id:"budgetAct", name:"Presupuesto - Actividad"},
        { id:"budgetLoc", name:"Presupuesto - Localizacion"},
        { id:"budgetADT", name:"Presupuesto - ADT"},
        { id:"budgetSDC", name:"Presupuesto - OrigenFondos"}
    ];

    constructor(view: IFollowupAdminMigration) {
        super(view);
        this.followupData = useSelector(this.context.followupStore.selectFollowupData);
        this.migrationData = useSelector(this.context.followupStore.selectMigrationData);
        this.formMasters = useSelector(this.context.followupStore.selectFormMasters);
        this.followupService = new FollowupService();
    }

    public async unSetLoading(){ 
        this.dispatcherUnsetSetLoading(); 
    }

    public async initialize(){ 
        this.dispatcherProgress(50);
        this.dispatcherPendingLoad(1);
        this.dispatcherSetLoading();

        if(!this.followupData.length) this.dispatcher(this.context.followupStore.getOnlyFollowupData()); 
        if(!this.formMasters) this.dispatcher(this.context.followupStore.getAllFormMasters()); 
        if(!this.migrationData) this.dispatcher(this.context.followupStore.getMigrationData()); 

        this.dispatcherProgress(80);  
    }

    public async initImportMigration(file: FileList){
        let migData: any = {}

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = e.target.result;

            const workbook = XLSX.read(data, { type: "array" });

            for (let i = 0; i < this.migrationSheets.length; i++) {
                const sh = this.migrationSheets[i];
                const ws = workbook.Sheets[sh.name];
                let json = XLSX.utils.sheet_to_json(ws);
                migData[sh.id] = json
            }

            this.saveMigrationData(migData)
        };
        reader.readAsArrayBuffer(file[0]);
    }

    public async initMigration(year: string){ 
        let dataFiltered = this.followupData.filter((x: any) => x.Anio === year)
        // let dataFiltered = this.followupData.filter((x: any) => (x.Id === 392 || x.Id === 560)) // Test

        let migData: any = new FollowupMigration()

        for (const item of dataFiltered) {
            let itemData = await this.getItemData(item, Number(year))

            let country = !!item.IdPais ? this.formMasters.countries?.find((c: any) => c.Id === Number(item.IdPais))?.Title : this.formMasters.regions?.find((r: any) => r.Id === Number(item.IdRegion))?.Title
            
            let initInfo: any = {
                subProyectoAnio: item.Id,
                country, 
                projectCode: item.PPresCodProy,
                grantcode: item.CodigoSubvencion || "",
            }

            if(!!itemData.step1?.year){
                migData.indLEPpal.push({
                    ...initInfo,
                    indicator: this.formMasters.strategicLine?.find((x: any) => x.Id === Number(itemData.step1?.genericInfo?.PPresIndicadorLineaEstrategica1Id))?.Title,
                    indStrategicLine: itemData.step1?.year?.PProaFRStep1Id,
                    indYearStrategicLine: itemData.step1?.year?.Id,
                    indBaseLine: itemData.step1?.genericInfo?.PFFRRLineaBase ?? " ",
                    indGoal: itemData.step1?.year?.PProaAnioValue ?? " ",
                    achievedGoal: " "
                })
            }

            for (const sMRI of itemData.stepMRI) {
                let auxInfo: any = {
                    ...initInfo,
                    indicator: "",
                    indStrategicLine: sMRI.year?.PProaStep4AdaptacionMRIId,
                    indYearStrategicLine: sMRI.year?.Id,
                    indBaseLine: sMRI?.genericInfo?.PFFRRLineaBase ?? " ",
                    indGoal: sMRI.year?.PProaAnioValue ?? " ",
                }
                if(!!sMRI.genericInfo?.PFFRRIndicadorResultadoIntermediId?.length && !!sMRI.year){
                    migData.indLEResI.push({
                        ...auxInfo,
                        indicator: this.formMasters.intermediateIndicatorResults.find((x: any) => x.Id === Number(sMRI.genericInfo.PFFRRIndicadorResultadoIntermediId[0]))?.Title,
                        achievedGoal: " "
                    })
                }else if(!!sMRI.genericInfo?.PFFRRIndicadorProductoId && !!sMRI.year){
                    migData.indLEProd.push({
                        ...auxInfo,
                        indicator: this.formMasters.productsIndicator.find((x: any) => x.Id === Number(sMRI.genericInfo.PFFRRIndicadorProductoId))?.Title,
                        achievedGoal: " "
                    })
                }else if(!!sMRI.genericInfo?.PPresIndicadorLineaEstrategicaId && !!sMRI.year){
                    migData.indLESec.push({
                        ...auxInfo,
                        indicator: this.formMasters.strategicLine.find((x: any) => x.Id === Number(sMRI.genericInfo.PPresIndicadorLineaEstrategicaId))?.Title,
                        achievedGoal: " "
                    })
                }
            }

            for (const s4 of itemData.step4) {
                let objetives = s4?.objetives || []
                for (const s4Obj of objetives) {
                    if(!!s4Obj.year){
                        migData.indObj.push({
                            ...initInfo,
                            objective: s4.genericInfo?.PProaObjetivo,
                            indicator: s4Obj.genericInfo?.PProaIndicadorObjetivo,
                            objectiveId: s4.genericInfo?.Id,
                            indicatorId: s4Obj.genericInfo?.Id,
                            indicatorAnioId: s4Obj.year?.Id,
                            indBaseLine: s4Obj.genericInfo?.PFFRRLineaBase ?? " ",
                            indGoal: s4Obj.year?.PProaAnioValue ?? " ",
                            achievedGoal: " "
                        })
                    }
                }

                let results = s4?.results || []
                for (const s4Res of results) {
                    let details = s4Res?.details || []
                    for (const s4ResDet of details) {
                        if(!!s4ResDet.year){
                            migData.indResEsp.push({
                                ...initInfo,
                                objective: s4.genericInfo?.PProaObjetivo,
                                expectResult: s4Res.genericInfo?.PProaResultadoEsperado,
                                indicator: s4ResDet.genericInfo?.PProaIndicadorResultadoEsperado,
                                objectiveId: s4.genericInfo?.Id,
                                expectResultId: s4Res.genericInfo?.Id,
                                indicatorId: s4ResDet.year?.PProaFEStep4ResultadoDetalleId,
                                indicatorAnioId: s4ResDet.year?.Id,
                                indBaseLine: s4ResDet.genericInfo?.PFFRRLineaBase ?? " ",
                                indGoal: s4ResDet.year?.PProaAnioValue ?? " ",
                                achievedGoal: " "
                            })
                        }
                    }

                    let activities = s4Res?.activities || []
                    for (const s4Act of activities) {
                        if(!!s4Act.year){
                            migData.indAct.push({
                                ...initInfo,
                                objective: s4.genericInfo?.PProaObjetivo,
                                expectResult: s4Res.genericInfo?.PProaResultadoEsperado,
                                indicator: s4Act.genericInfo?.PProaActividad,
                                objectiveId: s4.genericInfo?.Id,
                                expectResultId: s4Res.genericInfo?.Id,
                                indicatorId: s4Act.year?.PProaFEStep4ActividadId,
                                indicatorAnioId: s4Act.year?.Id,
                                indGoal: s4Act.year?.PFFRRSubTotal ?? " ",
                                achievedGoal: " "
                            })

                            migData.beneficiaries.push({
                                ...initInfo,
                                expectResult: s4Res.genericInfo?.PProaResultadoEsperado,
                                indicator: s4Act.genericInfo?.PProaActividad,
                                expectResultId: s4Res.genericInfo?.Id,
                                indicatorId: s4Act.year?.PProaFEStep4ActividadId,
                                indicatorAnioId: s4Act.year?.Id,
                                achievedBeneficiaries: " "
                            })

                            migData.budgetAct.push({
                                ...initInfo,
                                expectResult: s4Res.genericInfo?.PProaResultadoEsperado,
                                indicator: s4Act.genericInfo?.PProaActividad,
                                expectResultId: s4Res.genericInfo?.Id,
                                indicatorId: s4Act.year?.PProaFEStep4ActividadId,
                                indicatorAnioId: s4Act.year?.Id,
                                budgetYear: s4Act.year?.PProaAnioValue ?? " ",
                                executedYear: " "
                            })
                        }
                    }
                }
            }

            for (const s2 of itemData.step2) {
                if(!!s2.year){
                    migData.budgetImp.push({
                        ...initInfo,
                        implementator: s2.genericInfo?.OCTAVIASLEP?.Title,
                        implementatorId: s2.genericInfo?.Id,
                        implementatorAnioId: s2.year?.Id,
                        budgetYear: s2.year?.PProaAnioValue ?? " ",
                        executedYear: " "
                    })
                }
            }

            for (const s3 of itemData.step3) {
                if(!!s3.year){
                    if(!!s3.genericInfo?.PProaLocation){
                        migData.budgetLoc.push({
                            ...initInfo,
                            location: s3.genericInfo?.PProaLocation?.Title,
                            locationId: s3.genericInfo?.Id,
                            locationAnioId: s3.year?.Id,
                            budgetYear: s3.year?.PProaAnioValue ?? " ",
                            executedYear: " "
                        })
                    }else if(!!s3.genericInfo?.PProaAdt){
                        migData.budgetADT.push({
                            ...initInfo,
                            ADT: s3.genericInfo?.PProaAdt?.Title,
                            ADTId: s3.genericInfo?.Id,
                            ADTAnioId: s3.year?.Id,
                            budgetYear: s3.year?.PProaAnioValue ?? " ",
                            executedYear: " "
                        })
                    }
                }
            }

            for (const sSDC of itemData.stepSDC) {
                if(!!sSDC.year){
                    let counterpartType = this.formMasters.counterpartType?.find((x: any) => x.Id === sSDC?.genericInfo?.PProaTipoContrapartidaId)?.Title
                    let counterpart = (!!sSDC?.genericInfo?.OCTAVIACodigoFinanciacionId) ? this.formMasters.grantCodes?.find((x: any) => x.Id === sSDC?.genericInfo?.OCTAVIACodigoFinanciacionId)?.Title 
                    : (!!sSDC?.genericInfo?.OCTAVIAFinanciadorSapId) ? this.formMasters.allFinancers?.find((x: any) => x.Id === sSDC?.genericInfo?.OCTAVIAFinanciadorSapId)?.Title
                    : (!!sSDC?.genericInfo?.OCTAVIASLEPId) ? this.formMasters.slep?.find((x: any) => x.Id === sSDC?.genericInfo?.OCTAVIASLEPId)?.Title : sSDC?.genericInfo?.Title

                    migData.budgetSDC.push({
                        ...initInfo,
                        counterpartType,
                        counterpart,
                        counterpartId: sSDC.genericInfo?.Id,
                        counterpartAnioId: sSDC.year?.Id,
                        budgetYear: sSDC.year?.PProaAnioValue ?? " ",
                        executedYear: " "
                    })
                }
            }
        }

        var newwb = XLSX.utils.book_new();

        this.migrationSheets.forEach(element => {
            var sh = XLSX.utils.json_to_sheet(migData[element.id]);
            const header = Object.keys(migData?.[element.id]?.[0] || ""); // columns name
            var wscols = [];

            for (const _key of header) {
                let maxValue = _key?.length || 0

                for (const iterator of migData?.[element.id]) {
                    if(iterator?.[_key]?.toString()?.length > maxValue) maxValue = iterator?.[_key]?.toString()?.length
                }

                wscols.push({ width: ((maxValue + 5) > 30) ? 40 : maxValue + 5 })
            }

            const asd = Object.keys(sh || "");
            for (const iterator of asd) {
                if((!!sh[iterator]) && iterator != "!ref"){
                    if(sh[iterator]?.v === "achievedGoal" || sh[iterator]?.v === "executedYear" || sh[iterator]?.v === "achievedBeneficiaries"){
                        sh[iterator].s = {
                            font: {
                                name: 'arial',
                                sz: 11,
                                color: { rgb: "00000000" },
                                outline: true,
                            },
                            fill: {
                                fgColor: { rgb: "FFFF00" },
                                bgColor: { rgb: "FFFF00" }
                            },
                            alignment: {
                                horizontal: "center",
                            },
                            border: {
                                top: {style: "thin"},
                                bottom: {style: "thin"},
                                right: {style: "thin"},
                                left: {style: "thin"}
                            }
                        }
                    }else{
                        sh[iterator].s = {
                            font: {
                                name: 'arial',
                                sz: 11,
                                outline: true,
                            },
                            alignment: {
                                horizontal: "center",
                            },
                            border: {
                                top: {style: "thin"},
                                bottom: {style: "thin"},
                                right: {style: "thin"},
                                left: {style: "thin"}
                            }
                        }
                    }
                }
            }
            sh["!cols"] = wscols;

            XLSX.utils.book_append_sheet(newwb, sh, element.name);
        });

        const wbout = XLSX.write(newwb, {
            bookType: 'xlsx',
            bookSST: true,
            type: 'binary',
        });

        let url = window.URL.createObjectURL(
            new Blob([this.s2ab(wbout)], { type: 'application/octet-stream' })
        );

        this.download(url, 'PlantillaMigracion'+year+'.xlsx');

        this.dispatcher(this.context.followupStore.getMigrationData(true));
        this.view.state.setExportData(false)
        this.view.state.setYear(null)
        this.dispatcherUnsetSetLoading();
    }

    private s2ab(s: any) {
        const buf = new ArrayBuffer(s.length);
        
        const view = new Uint8Array(buf);
        
        for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
        
        return buf;
    }

    private download (url :any, name :any) {
        let a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        
        window.URL.revokeObjectURL(url);
    }

    public async getItemData(item: any, year: number){ 
        let subvCode = !!item.CodigoSubvencion ? this.formMasters.grantCodes.find((x: any) => x.Title === item.CodigoSubvencion)?.Id : null
        let itemData: any = {}

        let auxStep1 = this.migrationData.step1.filter((s1: any) => (s1.PPresPresupuestoId === item.IdProyecto && (!!subvCode ? subvCode === s1.PPresCodSubvencionId : !s1.PPresCodSubvencionId)))
        itemData.step1 = {
            genericInfo: auxStep1[0],
            year: this.migrationData.step1Anios.find((s1a: any) => (s1a.PProaFRStep1Id === auxStep1[0]?.Id && s1a.PPresAnioMeta === year))
        }

        let auxMRI = this.migrationData.stepMRI.filter((sMRI: any) => (sMRI.PPresPresupuestoId === item.IdProyecto && (!!subvCode ? subvCode === sMRI.PPresCodSubvencionId : !sMRI.PPresCodSubvencionId)))
        itemData.stepMRI = auxMRI.map((sMRI: any) => {
            return {
                genericInfo: sMRI,
                year: this.migrationData.stepMRIAnios.find((sMRIa: any) => (sMRIa.PProaStep4AdaptacionMRIId === sMRI?.Id && sMRIa.PPresAnioMeta === year))
            }
        })
        
        let auxStep4 = this.migrationData.step4.filter((s4: any) => (s4.PPresPresupuestoId === item.IdProyecto && (!!subvCode ? subvCode === s4.PPresCodSubvencionId : !s4.PPresCodSubvencionId)))
        itemData.step4 = auxStep4.map((s4: any) => {
            let auxObj = this.migrationData.step4Obj.filter((s4Obj: any) => (s4Obj.PProaFEStep4Id === s4?.Id))
            let objetives =auxObj.map((s4Obj: any) => {
                return {
                    genericInfo: s4Obj,
                    year: this.migrationData.step4ObjAnios.find((s4Obja: any) => (s4Obja.PProaFEStep4InidicadorId === s4Obj?.Id && s4Obja.PPresAnioMeta === year))
                }

            })

            let auxRes = this.migrationData.step4Res.filter((s4Res: any) => (s4Res.PProaFEStep4Id === s4?.Id))
            let results = auxRes.map((s4Res: any) => {
                let auxResDet = this.migrationData.step4ResDet.filter((s4ResDet: any) => (s4ResDet.PProaFEStep4ResultadosEsperadosId === s4Res?.Id))
                let details = auxResDet.map((auxResDet: any) => {
                    return {
                        genericInfo: auxResDet,
                        year: this.migrationData.step4ResDetAnios.find((s4ResDeta: any) => (s4ResDeta.PProaFEStep4ResultadoDetalleId === auxResDet?.Id && s4ResDeta.PPresAnioMeta === year))
                    }

                })

                let auxResAct = this.migrationData.step4Act.filter((s4ResAct: any) => (s4ResAct.PProaFEStep4ResultadosEsperadosId === s4Res?.Id))
                let activities = auxResAct.map((s4ResAct: any) => {
                    return {
                        genericInfo: s4ResAct,
                        year: this.migrationData.step4ActAnios.find((s4ResActa: any) => (s4ResActa.PProaFEStep4ActividadId === s4ResAct?.Id && s4ResActa.PPresAnioMeta === year))
                    }

                })

                return {
                    genericInfo: s4Res,
                    details,
                    activities
                }

            })

            return {
                genericInfo: s4,
                objetives,
                results
            }

        })

        let auxStep2 = this.migrationData.step2.filter((s2: any) => (s2.PPresPresupuestoId === item.IdProyecto && (!!subvCode ? subvCode === s2.PPresCodSubvencionId : !s2.PPresCodSubvencionId)))
        itemData.step2 = auxStep2.map((s2: any) => {
            return {
                genericInfo: s2,
                year: this.migrationData.step2Anios.find((s2a: any) => (s2a.PProaFRStep2Id === s2?.Id && s2a.PPresAnioMeta === year))
            }
        })

        let auxStep3 = this.migrationData.step3.filter((s3: any) => (s3.PPresPresupuestoId === item.IdProyecto && (!!subvCode ? subvCode === s3.PPresCodSubvencionId : !s3.PPresCodSubvencionId)))
        itemData.step3 = auxStep3.map((s3: any) => {
            return {
                genericInfo: s3,
                year: this.migrationData.step3Anios.find((s3a: any) => (s3a.PProaFRStep3Id === s3?.Id && s3a.PPresAnioMeta === year))
            }
        })

        let auxStepSDC = this.migrationData.stepSDC.filter((sSDC: any) => (sSDC.PPresPresupuestoId === item.IdProyecto && (!!subvCode ? subvCode === sSDC.PPresCodSubvencionId : !sSDC.PPresCodSubvencionId)))
        itemData.stepSDC = auxStepSDC.map((sSDC: any) => {
            return {
                genericInfo: sSDC,
                year: this.migrationData.stepSDCAnios.find((sSDCa: any) => (sSDCa.PProaContrapartidaId === sSDC?.Id && sSDCa.PPresAnioMeta === year))
            }
        })

        return itemData
    }

    public async saveMigrationData(migData: FollowupMigration){ 
        let migDataAdd: FollowupMigration = new FollowupMigration()

        let migDataUpd: FollowupMigration = new FollowupMigration()

        for (const indLEPpal of migData.indLEPpal) {
            indLEPpal.achievedGoal = typeof indLEPpal.achievedGoal === "string" ? indLEPpal.achievedGoal.trim() : indLEPpal.achievedGoal
            if(!Utilities.isNullEmptyObject(indLEPpal.achievedGoal)){
                let itemExist = this.migrationData.indLEPpal.find((a: any) => (a.SubProyectoAnioId === indLEPpal.subProyectoAnio && a.IndicadorLineaEstrategicaAniosId === indLEPpal.indYearStrategicLine))
                let itemToSave: any = { MetaAlcanzada: indLEPpal.achievedGoal || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.indLEPpal.push(itemToSave)
                else{
                    itemToSave.PPresIndicadorLineaEstrategicaId = this.formMasters.strategicLine?.find((x: any) => (x.Title === indLEPpal.indicator || x.OCTAVIANombreEn === indLEPpal.indicator || x.OCTAVIANombreFR === indLEPpal.indicator || x.OCTAVIANombrePT === indLEPpal.indicator))?.Id
                    itemToSave.IndicadorLineaEstrategicaId = Number(indLEPpal.indStrategicLine)
                    itemToSave.IndicadorLineaEstrategicaAniosId = Number(indLEPpal.indYearStrategicLine)
                    itemToSave.SubProyectoAnioId = Number(indLEPpal.subProyectoAnio)
                    migDataAdd.indLEPpal.push(itemToSave)
                }
            }
        }

        for (const indLESec of migData.indLESec) {
            indLESec.achievedGoal = typeof indLESec.achievedGoal === "string" ? indLESec.achievedGoal.trim() : indLESec.achievedGoal
            if(!Utilities.isNullEmptyObject(indLESec.achievedGoal)){
                let itemExist = this.migrationData.indLESec.find((a: any) => (a.SubProyectoAnioId === indLESec.subProyectoAnio && a.IndLineaEstrategicaAniosSecundarId === indLESec.indYearStrategicLine))
                let itemToSave: any = { MetaAlcanzada: indLESec.achievedGoal || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.indLESec.push(itemToSave)
                else{
                    itemToSave.PPresIndicadorLineaEstrategicaId = this.formMasters.strategicLine?.find((x: any) => (x.Title === indLESec.indicator || x.OCTAVIANombreEn === indLESec.indicator || x.OCTAVIANombreFR === indLESec.indicator || x.OCTAVIANombrePT === indLESec.indicator))?.Id
                    itemToSave.IndicadorLineaEstrategicaSecundaId = Number(indLESec.indStrategicLine)
                    itemToSave.IndLineaEstrategicaAniosSecundarId = Number(indLESec.indYearStrategicLine)
                    itemToSave.SubProyectoAnioId = Number(indLESec.subProyectoAnio)
                    migDataAdd.indLESec.push(itemToSave)
                }
            }
        }

        for (const indLEResI of migData.indLEResI) {
            indLEResI.achievedGoal = typeof indLEResI.achievedGoal === "string" ? indLEResI.achievedGoal.trim() : indLEResI.achievedGoal
            if(!Utilities.isNullEmptyObject(indLEResI.achievedGoal)){
                let itemExist = this.migrationData.indLEResI.find((a: any) => (a.SubProyectoAnioId === indLEResI.subProyectoAnio && a.IndicadorInstitucionalResultados0Id === indLEResI.indYearStrategicLine))
                let itemToSave: any = { MetaAlcanzada: indLEResI.achievedGoal || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.indLEResI.push(itemToSave)
                else{
                    let resultIndId = this.formMasters.intermediateIndicatorResults?.find((x: any) => (x.Title === indLEResI.indicator || x.OCTAVIANombreEn === indLEResI.indicator || x.OCTAVIANombreFR === indLEResI.indicator || x.OCTAVIANombrePT === indLEResI.indicator))?.Id
                
                    itemToSave.PFFRRIndicadorResultadoIntermediId = { '__metadata': { type: 'Collection(Edm.Int32)' }, results: [ resultIndId ] }
                    itemToSave.IndicadorInstitucionalResultados0Id = Number(indLEResI.indYearStrategicLine)
                    itemToSave.SubProyectoAnioId = Number(indLEResI.subProyectoAnio)
                    migDataAdd.indLEResI.push(itemToSave)
                }
            }
        }

        for (const indLEProd of migData.indLEProd) {
            indLEProd.achievedGoal = typeof indLEProd.achievedGoal === "string" ? indLEProd.achievedGoal.trim() : indLEProd.achievedGoal
            if(!Utilities.isNullEmptyObject(indLEProd.achievedGoal)){
                let itemExist = this.migrationData.indLEProd.find((a: any) => (a.SubProyectoAnioId === indLEProd.subProyectoAnio && a.IndicadorInstitucionalProductosAId === indLEProd.indYearStrategicLine))
                let itemToSave: any = { MetaAlcanzada: indLEProd.achievedGoal || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.indLEProd.push(itemToSave)
                else{
                    itemToSave.PFFRRIndicadorProductoId = this.formMasters.productsIndicator?.find((x: any) => (x.Title === indLEProd.indicator || x.OCTAVIANombreEn === indLEProd.indicator || x.OCTAVIANombreFR === indLEProd.indicator || x.OCTAVIANombrePT === indLEProd.indicator))?.Id
                    itemToSave.IndicadorInstitucionalProductosId = Number(indLEProd.indStrategicLine)
                    itemToSave.IndicadorInstitucionalProductosAId = Number(indLEProd.indYearStrategicLine)
                    itemToSave.SubProyectoAnioId = Number(indLEProd.subProyectoAnio)
                    migDataAdd.indLEProd.push(itemToSave)
                }
            }
        }

        for (const indObj of migData.indObj) {
            indObj.achievedGoal = typeof indObj.achievedGoal === "string" ? indObj.achievedGoal.trim() : indObj.achievedGoal
            if(!Utilities.isNullEmptyObject(indObj.achievedGoal)){
                let itemExist = this.migrationData.indObj.find((a: any) => (a.SubProyectoAnioId === indObj.subProyectoAnio && a.IndicadorObjetivoAnioId === indObj.indicatorAnioId))
                let itemToSave: any = { MetaAlcanzada: indObj.achievedGoal || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.indObj.push(itemToSave)
                else{
                    itemToSave.ObjetivoEspecificoId = Number(indObj.objectiveId)
                    itemToSave.IndicadorObjetivoId = Number(indObj.indicatorId)
                    itemToSave.IndicadorObjetivoAnioId = Number(indObj.indicatorAnioId)
                    itemToSave.SubProyectoAnioId = Number(indObj.subProyectoAnio)
                    migDataAdd.indObj.push(itemToSave)
                }
            }
        }

        for (const indResEsp of migData.indResEsp) {
            indResEsp.achievedGoal = typeof indResEsp.achievedGoal === "string" ? indResEsp.achievedGoal.trim() : indResEsp.achievedGoal
            if(!Utilities.isNullEmptyObject(indResEsp.achievedGoal)){
                let itemExist = this.migrationData.indResEsp.find((a: any) => (a.SubProyectoAnioId === indResEsp.subProyectoAnio && a.IndicadorResultadoEsperadoAnioId === indResEsp.indicatorAnioId))
                let itemToSave: any = { MetaAlcanzada: indResEsp.achievedGoal || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.indResEsp.push(itemToSave)
                else{
                    itemToSave.ObjetivoEspecificoId = Number(indResEsp.objectiveId)
                    itemToSave.ResultadoEsperadoId = Number(indResEsp.expectResultId)
                    itemToSave.IndicadorResultadoEsperadoId = Number(indResEsp.indicatorId)
                    itemToSave.IndicadorResultadoEsperadoAnioId = Number(indResEsp.indicatorAnioId)
                    itemToSave.SubProyectoAnioId = Number(indResEsp.subProyectoAnio)
                    migDataAdd.indResEsp.push(itemToSave)
                }
            }
        }

        for (const indAct of migData.indAct) {
            indAct.achievedGoal = typeof indAct.achievedGoal === "string" ? indAct.achievedGoal.trim() : indAct.achievedGoal
            if(!Utilities.isNullEmptyObject(indAct.achievedGoal)){
                let itemExist = this.migrationData.indAct.find((a: any) => (a.SubProyectoAnioId === indAct.subProyectoAnio && a.IndicadorActividadAnioId === indAct.indicatorAnioId))
                let itemToSave: any = { MetaAlcanzada: indAct.achievedGoal || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.indAct.push(itemToSave)
                else{
                    itemToSave.ObjetivoEspecificoId = Number(indAct.objectiveId)
                    itemToSave.ResultadoEsperadoId = Number(indAct.expectResultId)
                    itemToSave.IndicadorActividadId = Number(indAct.indicatorId)
                    itemToSave.IndicadorActividadAnioId = Number(indAct.indicatorAnioId)
                    itemToSave.SubProyectoAnioId = Number(indAct.subProyectoAnio)
                    migDataAdd.indAct.push(itemToSave)
                }
            }
        }

        for (const beneficiaries of migData.beneficiaries) {
            beneficiaries.achievedBeneficiaries = typeof beneficiaries.achievedBeneficiaries === "string" ? beneficiaries.achievedBeneficiaries.trim() : beneficiaries.achievedBeneficiaries
            if(!Utilities.isNullEmptyObject(beneficiaries.achievedBeneficiaries)){
                let itemExist = this.migrationData.beneficiaries.find((a: any) => (a.SubProyectoAnioId === beneficiaries.subProyectoAnio && a.IndicadorActividadAnioId === beneficiaries.indicatorAnioId))
                let itemToSave: any = { BeneficiariosAlcanzados: beneficiaries.achievedBeneficiaries || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.beneficiaries.push(itemToSave)
                else{
                    itemToSave.PProaFEStep4ResultadosEsperadosId = Number(beneficiaries.expectResultId)
                    itemToSave.IndicadorActividadId = Number(beneficiaries.indicatorId)
                    itemToSave.IndicadorActividadAnioId = Number(beneficiaries.indicatorAnioId)
                    itemToSave.SubProyectoAnioId = Number(beneficiaries.subProyectoAnio)
                    migDataAdd.beneficiaries.push(itemToSave)
                }
            }
        }

        for (const budgetImp of migData.budgetImp) { 
            budgetImp.executedYear = typeof budgetImp.executedYear === "string" ? budgetImp.executedYear.trim() : budgetImp.executedYear
            if(!Utilities.isNullEmptyObject(budgetImp.executedYear)){
                let itemExist = this.migrationData.budgetImp.find((a: any) => (a.SubProyectoAnioId === budgetImp.subProyectoAnio && a.PProaFRStep2AniosId === budgetImp.implementatorAnioId))
                let itemToSave: any = { PProaEjecutadoAnio: budgetImp.executedYear || 0, SumaReal: budgetImp.executedYear || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.budgetImp.push(itemToSave)
                else{
                    itemToSave.PProaFRStep2Id = Number(budgetImp.implementatorId)
                    itemToSave.PProaFRStep2AniosId = Number(budgetImp.implementatorAnioId)
                    itemToSave.SubProyectoAnioId = Number(budgetImp.subProyectoAnio)
                    migDataAdd.budgetImp.push(itemToSave)
                }
            }
        }

        for (const budgetAct of migData.budgetAct) {
            budgetAct.executedYear = typeof budgetAct.executedYear === "string" ? budgetAct.executedYear.trim() : budgetAct.executedYear
            if(!Utilities.isNullEmptyObject(budgetAct.executedYear)){
                let itemExist = this.migrationData.budgetAct.find((a: any) => (a.SubProyectoAnioId === budgetAct.subProyectoAnio && a.IndicadorActividadAnioId === budgetAct.indicatorAnioId))
                let itemToSave: any = { PProaEjecutadoAnio: budgetAct.executedYear || 0, SumaReal: budgetAct.executedYear || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.budgetAct.push(itemToSave)
                else{
                    itemToSave.PProaFEStep4ResultadosEsperadosId = Number(budgetAct.expectResultId)
                    itemToSave.IndicadorActividadId = Number(budgetAct.indicatorId)
                    itemToSave.IndicadorActividadAnioId = Number(budgetAct.indicatorAnioId)
                    itemToSave.SubProyectoAnioId = Number(budgetAct.subProyectoAnio)
                    migDataAdd.budgetAct.push(itemToSave)
                }
            }
        }

        for (const budgetLoc of migData.budgetLoc) {
            budgetLoc.executedYear = typeof budgetLoc.executedYear === "string" ? budgetLoc.executedYear.trim() : budgetLoc.executedYear
            if(!Utilities.isNullEmptyObject(budgetLoc.executedYear)){
                let itemExist = this.migrationData.budgetLoc.find((a: any) => (a.SubProyectoAnioId === budgetLoc.subProyectoAnio && a.PProaFRStep3AniosId === budgetLoc.locationAnioId))
                let itemToSave: any = { PProaEjecutadoAnio: budgetLoc.executedYear || 0, SumaReal: budgetLoc.executedYear || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.budgetLoc.push(itemToSave)
                else{
                    itemToSave.PProaFRStep3Id = Number(budgetLoc.locationId)
                    itemToSave.PProaFRStep3AniosId = Number(budgetLoc.locationAnioId)
                    itemToSave.SubProyectoAnioId = Number(budgetLoc.subProyectoAnio)
                    migDataAdd.budgetLoc.push(itemToSave)
                }
            }
        }

        for (const budgetADT of migData.budgetADT) {
            budgetADT.executedYear = typeof budgetADT.executedYear === "string" ? budgetADT.executedYear.trim() : budgetADT.executedYear
            if(!Utilities.isNullEmptyObject(budgetADT.executedYear)){
                let itemExist = this.migrationData.budgetADT.find((a: any) => (a.SubProyectoAnioId === budgetADT.subProyectoAnio && a.PProaFRStep3AniosId === budgetADT.ADTAnioId))
                let itemToSave: any = { PProaEjecutadoAnio: budgetADT.executedYear || 0, SumaReal: budgetADT.executedYear || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.budgetADT.push(itemToSave)
                else{
                    itemToSave.PProaFRStep3Id = Number(budgetADT.ADTId)
                    itemToSave.PProaFRStep3AniosId = Number(budgetADT.ADTAnioId)
                    itemToSave.SubProyectoAnioId = Number(budgetADT.subProyectoAnio)
                    migDataAdd.budgetADT.push(itemToSave)
                }
            }
        }

        for (const budgetSDC of migData.budgetSDC) {
            budgetSDC.executedYear = typeof budgetSDC.executedYear === "string" ? budgetSDC.executedYear.trim() : budgetSDC.executedYear
            if(!Utilities.isNullEmptyObject(budgetSDC.executedYear)){
                let itemExist = this.migrationData.budgetSDC.find((a: any) => (a.SubProyectoAnioId === budgetSDC.subProyectoAnio && a.PProaContrapartidaAnioId === budgetSDC.counterpartAnioId))
                let itemToSave: any = { PProaEjecutadoAnio: budgetSDC.executedYear || 0 , SumaReal: budgetSDC.executedYear || 0, Id: itemExist?.Id }
                if(!!itemExist) migDataUpd.budgetSDC.push(itemToSave)
                else{
                    itemToSave.PProaContrapartidaId = Number(budgetSDC.counterpartId)
                    itemToSave.PProaContrapartidaAnioId = Number(budgetSDC.counterpartAnioId)
                    itemToSave.SubProyectoAnioId = Number(budgetSDC.subProyectoAnio)
                    migDataAdd.budgetSDC.push(itemToSave)
                }
            }
        }

        try {
            await Promise.all([
                this.followupService.addIndexFollowup(migDataAdd.indLEPpal, Enums.ListTypes.SeguimientoLineaPrincipal),
                this.followupService.addIndexFollowup(migDataAdd.indLESec, Enums.ListTypes.SeguimientoLineaSecundaria),
                this.followupService.addIndexFollowup(migDataAdd.indLEResI, Enums.ListTypes.SeguimientoResultadosInstitucionales),
                this.followupService.addIndexFollowup(migDataAdd.indLEProd, Enums.ListTypes.SeguimientoProductosInstitucionales),
                this.followupService.addIndexFollowup(migDataAdd.indObj, Enums.ListTypes.SeguimientoIndObjEspecificos),
                this.followupService.addIndexFollowup(migDataAdd.indResEsp, Enums.ListTypes.SeguimientoResultadosEsperados),
                this.followupService.addIndexFollowup(migDataAdd.indAct, Enums.ListTypes.SeguimientoActividades),
    
                this.followupService.addIndexFollowup(migDataAdd.beneficiaries, Enums.ListTypes.BeneficiariesByResult),
                this.followupService.addIndexFollowup(migDataAdd.budgetImp, Enums.ListTypes.FinancieraPresupuestoImplementadores),
                this.followupService.addIndexFollowup(migDataAdd.budgetAct, Enums.ListTypes.FinancieraResultadosEsperados),
                this.followupService.addIndexFollowup(migDataAdd.budgetLoc, Enums.ListTypes.FinancieraPresupuestoLocalizaciones),
                this.followupService.addIndexFollowup(migDataAdd.budgetADT, Enums.ListTypes.FinancieraPresupuestoADT),
                this.followupService.addIndexFollowup(migDataAdd.budgetSDC, Enums.ListTypes.FinancieraPresupuestoSDC),
    
                this.followupService.updateIndexFollowup(migDataUpd.indLEPpal, Enums.ListTypes.SeguimientoLineaPrincipal),
                this.followupService.updateIndexFollowup(migDataUpd.indLESec, Enums.ListTypes.SeguimientoLineaSecundaria),
                this.followupService.updateIndexFollowup(migDataUpd.indLEResI, Enums.ListTypes.SeguimientoResultadosInstitucionales),
                this.followupService.updateIndexFollowup(migDataUpd.indLEProd, Enums.ListTypes.SeguimientoProductosInstitucionales),
                this.followupService.updateIndexFollowup(migDataUpd.indObj, Enums.ListTypes.SeguimientoIndObjEspecificos),
                this.followupService.updateIndexFollowup(migDataUpd.indResEsp, Enums.ListTypes.SeguimientoResultadosEsperados),
                this.followupService.updateIndexFollowup(migDataUpd.indAct, Enums.ListTypes.SeguimientoActividades),
    
                this.followupService.updateIndexFollowup(migDataUpd.beneficiaries, Enums.ListTypes.BeneficiariesByResult),
                this.followupService.updateIndexFollowup(migDataUpd.budgetImp, Enums.ListTypes.FinancieraPresupuestoImplementadores),
                this.followupService.updateIndexFollowup(migDataUpd.budgetAct, Enums.ListTypes.FinancieraResultadosEsperados),
                this.followupService.updateIndexFollowup(migDataUpd.budgetLoc, Enums.ListTypes.FinancieraPresupuestoLocalizaciones),
                this.followupService.updateIndexFollowup(migDataUpd.budgetADT, Enums.ListTypes.FinancieraPresupuestoADT),
                this.followupService.updateIndexFollowup(migDataUpd.budgetSDC, Enums.ListTypes.FinancieraPresupuestoSDC),
            ]);

            Utilities.notifySuccess("La información ha sido migrada y guardada con éxito.", "Correcto")
        } catch (error) {
            Utilities.notifyError("Ha ocurrido un error guardando la información.", "Error")
            console.log("error")
        }

        this.dispatcher(this.context.followupStore.getMigrationData(true));
        this.dispatcherUnsetSetLoading();
        this.view.state.setImportData(false)
        this.view.state.setFileValue("")
        this.view.state.setFile(null)
    }
}

export default AdminFollowupMigration;
